import { inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { lastValueFrom, map, take } from 'rxjs';

import { fetchIfNotLoaded } from '@core/ngrx-legacy';
import { TellentDevFlagsActions, TellentDevFlagsSelectors } from '@core/tellent-dev-flags-data';
import { TellentDevFlag } from '@core/tellent-types';

export const injectDevFlagsObservable = () => {
  const store = inject(Store);
  return store.pipe(
    fetchIfNotLoaded(store.select(TellentDevFlagsSelectors.meta), () =>
      store.dispatch(TellentDevFlagsActions.fetch()),
    ),
    select(TellentDevFlagsSelectors.data),
  );
};

export const injectDevFlagsPromise = () => lastValueFrom(injectDevFlagsObservable().pipe(take(1)));

export const injectDevFlagObservable = (flag: TellentDevFlag) => {
  const store = inject(Store);
  return store.pipe(
    fetchIfNotLoaded(store.select(TellentDevFlagsSelectors.meta), () =>
      store.dispatch(TellentDevFlagsActions.fetch()),
    ),
    select(TellentDevFlagsSelectors.isEnabled({ flag })),
  );
};

export const injectDevFlagSignal = (flag: TellentDevFlag) =>
  toSignal(injectDevFlagObservable(flag));

export const injectDevFlagPromise = (flag: TellentDevFlag) =>
  lastValueFrom(injectDevFlagObservable(flag).pipe(take(1)));

export function devFlagGuard(flag: TellentDevFlag): CanActivateFn {
  return () => {
    const store = inject(Store);
    const router = inject(Router);

    return store.pipe(
      fetchIfNotLoaded(store.select(TellentDevFlagsSelectors.meta), () =>
        store.dispatch(TellentDevFlagsActions.fetch()),
      ),
      select(TellentDevFlagsSelectors.isEnabled({ flag })),
      // TODO: should be more generic - probably like devFlagCanMatch
      map(isEnabled => isEnabled || router.createUrlTree(['/dashboard'])),
    );
  };
}

// Checks if TellentDevFlag is enabled and returns true if it matches the expected value
export function devFlagCanMatch(flag: TellentDevFlag, expectedValue: boolean): CanMatchFn {
  return () => {
    const store = inject(Store);

    return store.pipe(
      fetchIfNotLoaded(store.select(TellentDevFlagsSelectors.meta), () =>
        store.dispatch(TellentDevFlagsActions.fetch()),
      ),
      select(TellentDevFlagsSelectors.isEnabled({ flag })),
      map(isEnabled => isEnabled === expectedValue),
    );
  };
}
